/**
 * Centralized logic for managing component visibility based on module_name URL parameter
 * If module_name is specified, only that component displays. Otherwise, all components show.
 */

import { useEffect, useState } from "react";

/**
 * React hook to determine if a component should be visible
 * Reads the 'module_name' query parameter from the URL
 * @param {string} componentModuleName - The unique MODULE_NAME identifier for this component
 * @returns {boolean} True if component should be visible, false otherwise
 */
export const useModuleVisibility = (componentModuleName) => {
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        const updateVisibility = () => {
            if (typeof window === 'undefined') {
                setIsVisible(true);
                return;
            }

            const params = new URLSearchParams(window.location.search);
            const requestedModule = params.get('module_name');

            // If no module_name parameter, show all components
            if (!requestedModule) {
                setIsVisible(true);
                return;
            }

            // Show only the requested module
            setIsVisible(requestedModule === componentModuleName);
        };

        updateVisibility();

        // Listen for URL changes (browser back/forward, manual navigation)
        window.addEventListener('popstate', updateVisibility);
        return () => window.removeEventListener('popstate', updateVisibility);
    }, [componentModuleName]);

    return isVisible;
};

/**
 * Set the module_name URL parameter to display a specific module
 * @param {string} moduleName - The MODULE_NAME to display
 * @param {object} additionalParams - Additional query parameters to preserve
 */
export const setActiveModule = (moduleName, additionalParams = {}) => {
    const params = new URLSearchParams(window.location.search);
    
    if (moduleName) {
        params.set('module_name', moduleName);
    } else {
        params.delete('module_name');
    }

    // Preserve other parameters
    Object.entries(additionalParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
            params.set(key, value);
        }
    });

    window.history.pushState({}, "", `?${params.toString()}`);
};

/**
 * Clear the module_name parameter to display all components
 * @param {object} additionalParams - Additional query parameters to preserve
 */
export const clearActiveModule = (additionalParams = {}) => {
    setActiveModule(null, additionalParams);
};

/**
 * Get the currently active module from URL
 * @returns {string|null} The active module name or null if none specified
 */
export const getActiveModule = () => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('module_name');
};

/**
 * Higher-order component wrapper to conditionally render a component
 * Usage: const VisibleComponent = withModuleVisibility(YourComponent, 'ModuleNameValue');
 */
export const withModuleVisibility = (Component, moduleName) => {
    return (props) => {
        const isVisible = useModuleVisibility(moduleName);
        return isVisible ? <Component {...props} /> : null;
    };
};